import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import type { MouseEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { GRID_DETAIL_PANEL_TOGGLE_FIELD, GridRowId } from '@mui/x-data-grid-pro';
import { DataGridDetailPanel, type DataGridDetailPanelItem } from '@aeg-presents/shared-components/DataGridDetailPanel';
import {
  DataGridMasterDetail,
  MasterDetailExpandButton,
  type DataGridMasterDetailColumn,
  type DataGridMasterDetailRow,
} from '@aeg-presents/shared-components/DataGridMasterDetail';
import { type TicketingSalesVenue, useTicketingSalesStore } from '@stores/ticketingSales';
import { TicketShow } from '@app-types/ticketSales';
import { DATE_FORMATS, formatDate } from '@utils/dateFormatters';
import { AllocationTable } from './AllocationTable';

export type TicketSalesGridRow = DataGridMasterDetailRow & {
  name: string;
  showCount: number;
  firstEventDate: string | null;
  shows: TicketShow[];
};

function toggleExpandedRow(
  rowId: GridRowId,
  expandedRows: Set<GridRowId>,
  setExpandedRows: (ids: Set<GridRowId>) => void,
) {
  const next = new Set(expandedRows);
  if (next.has(rowId)) {
    next.delete(rowId);
  } else {
    next.add(rowId);
  }
  setExpandedRows(next);
}

function buildShowDetailItems(shows: TicketShow[]): DataGridDetailPanelItem[] {
  return shows.map((show) => ({
    id: show.id,
    visible: true,
    label: (
      <span data-testid={`show-label-${show.id}`}>
        {`Show ${show.showNumber} (${formatDate(show.eventDate, DATE_FORMATS.DAY_MONTH_YEAR, 'TBD')})`}
      </span>
    ),
    content: <AllocationTable show={show} />,
  }));
}

export function TicketSalesGrid() {
  const venuesById = useTicketingSalesStore((state) => state.ticketingSales.venuesById);
  const venuesIdOrder = useTicketingSalesStore((state) => state.ticketingSales.venuesIdOrder);
  const venues = useMemo(
    () =>
      venuesIdOrder
        .map((venueId) => venuesById[venueId])
        .filter((venue): venue is TicketingSalesVenue => Boolean(venue)),
    [venuesById, venuesIdOrder],
  );
  const [expandedRows, setExpandedRows] = useState<Set<GridRowId>>(() => new Set(venues.map((venue) => venue.id)));

  const handleExpandClick = useCallback(
    (rowId: GridRowId) => {
      toggleExpandedRow(rowId, expandedRows, setExpandedRows);
    },
    [expandedRows],
  );

  const rows: TicketSalesGridRow[] = useMemo(
    () =>
      venues.map((venue) => ({
        id: venue.id,
        name: venue.name,
        showCount: venue.showCount,
        firstEventDate: venue.firstEventDate,
        shows: venue.showsIdOrder.map((showId) => venue.showsById[showId]),
        visible: true,
      })),
    [venues],
  );

  const columns: DataGridMasterDetailColumn<TicketSalesGridRow>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Venue',
        width: 160,
        sortable: false,
        visible: true,
        renderCell: (params) => <span data-testid={`venue-name-${params.row.id}`}>{params.row.name}</span>,
      },
      {
        field: 'showCount',
        headerName: 'Shows',
        width: 160,
        sortable: false,
        visible: true,
        renderCell: (params) => <span data-testid={`venue-shows-${params.row.id}`}>{params.row.showCount}</span>,
      },
      {
        field: 'firstEventDate',
        headerName: 'First event date',
        width: 134,
        sortable: false,
        visible: true,
        renderCell: (params) => (
          <span data-testid={`venue-first-event-${params.row.id}`}>
            {formatDate(params.row.firstEventDate, DATE_FORMATS.DAY_MONTH_YEAR_WITH_DAY, 'TBD')}
          </span>
        ),
      },
      {
        field: 'spacer',
        headerName: '',
        flex: 1,
        minWidth: 196,
        sortable: false,
        disableColumnMenu: true,
        visible: true,
        renderCell: () => null,
      },
      {
        field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
        headerName: '',
        width: 48,
        sortable: false,
        disableColumnMenu: true,
        cellClassName: 'expand-toggle-cell',
        visible: true,
        renderCell: (params) => {
          const isExpanded = expandedRows.has(params.id);

          return (
            <MasterDetailExpandButton
              size="small"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
              aria-expanded={isExpanded}
              onClick={(event: MouseEvent<HTMLElement>) => {
                event.stopPropagation();
                handleExpandClick(params.id);
              }}
              data-testid="expandable-section-caret"
            >
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </MasterDetailExpandButton>
          );
        },
      },
    ],
    [expandedRows, handleExpandClick],
  );

  const getDetailPanelContent = useCallback(
    ({ row }: { row: TicketSalesGridRow }) => (
      <DataGridDetailPanel
        dataTestID={`venue-content-${row.id}`}
        visible={true}
        collapsed={false}
        items={buildShowDetailItems(row.shows)}
      />
    ),
    [],
  );

  return (
    <DataGridMasterDetail
      dataTestID="ticket-sales-grid"
      rows={rows}
      columns={columns}
      collapsed={false}
      detailPanelExpandedRowIds={expandedRows}
      onDetailPanelExpandedRowIdsChange={setExpandedRows}
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={() => 'auto'}
      getRowClassName={(params) => `venue-header-${params.id}`}
    />
  );
}
